import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClassnames } from 'hook/use-classnames';
import UI from 'component/ui';
import InputEvents from 'component/form/input-events/index-tsx-async';
import InputAlbums from 'component/form/input-albums';
import InputYears from 'component/form/input-years';
import InputForm from 'component/form/input';
import Checkbox from 'component/form/checkbox';

import { IValue } from 'component/form/input-years/types';
import { IDefaultPresets, IPresetProps } from './types';
import style from './index.pcss';
import { Link } from 'react-router-dom';
import Button from 'component/button';
import InputSelect from '../input-events';

const Presets = (props: IPresetProps) => {
    const cn = useClassnames(style);
    const { t } = useTranslation();

    const [year, setYear] = useState<number>(0);
    const [defaultPreset, setDefaultPreset] = useState<IDefaultPresets>({});

    const onChangeYear = (value: IValue): void => {
        if (value) {
            setYear(Number(value?.value));
        } else {
            setYear(0);
        }
    };

    useEffect(() => {
        const defaultParams = localStorage.getItem('upload_params');
        const defaultVideoParams = localStorage.getItem('upload_video_params');
        if (defaultParams && !props.isPhotoPreview) {
            const params = JSON.parse(defaultParams);
            setDefaultPreset(params);
            setYear(Number(params.year.value));
        } else if (defaultVideoParams && props.isPhotoPreview) {
            const params = JSON.parse(defaultVideoParams);
            setDefaultPreset(params);
            setYear(Number(params.year.value));
        }

    }, []);

    const priceCheckValidity = () => {
        const fields = props.registry.form.getFields();

        if(fields) {
            const price = fields.get('upload_price');

            if (price) {
                if (Number(price.value) < 20) {
                    return {
                        isValid: false,
                        error: 'Минимальная стоимость 20 руб.'
                    };
                }

                return {
                    isValid: true,
                    error: ''
                };
            }
        }
    };

    const elTournament = useMemo(() => {
        let defaultValue;

        if (props.uploadingInfo) {
            defaultValue = {
                label: props.uploadingInfo.event?.label ? props.uploadingInfo.event.label : '',
                value: props.uploadingInfo.event?.value ? props.uploadingInfo.event.value : ''
            };
        } else if (props.isHasVideo) {
            defaultValue = {
                label: props.isHasVideo.event.name ? props.isHasVideo.event.name : '',
                value: props.isHasVideo.event.id ? props.isHasVideo.event.id.toString() : ''
            };
        } else if (defaultPreset.event?.label && defaultPreset.event.value) {
            defaultValue = {
                label: defaultPreset.event?.label,
                value: defaultPreset.event.value
            };
        }

        return (
            <InputSelect
                registry={props.registry.field}
                name="upload_tournament"
                clearable={true}
                excludeEmpty={false}
                year={year}
                children={t('route.upload.sidebar.modal.event')}
                direction="column"
                disabled={!Boolean(year) || props.isPhotoPreview && !props.isHasAlbum}
                defaultValue={defaultValue}
            />
        );
    }, [year, props.isHasAlbum]);

    const elSaveParams = useMemo(() => {
        if (props.isSaveParams) {
            return (
                <Checkbox
                    className={cn('input__sidebar-price')}
                    name={'save_params'}
                    registry={props.registry.field}
                    children={'Сохранить параметры загрузки'}
                    defaultValue={!!defaultPreset.album}
                />
            );
        }
    }, [defaultPreset, props.isSaveParams]);

    let defaultYear;

    if (props.uploadingInfo) {
        defaultYear = {
            label: props.uploadingInfo.year?.label ? props.uploadingInfo.year?.label : '',
            value: props.uploadingInfo.year?.value ? props.uploadingInfo.year?.value : ''
        };
    } else if (defaultPreset.year?.label && defaultPreset.year.value) {
        defaultYear = {
            label: defaultPreset.year?.label,
            value: defaultPreset.year.value
        };
    }

    let defaultAlbum;

    if (props.isHasAlbum) {
        defaultAlbum = {
            label: props.isHasAlbum.name,
            value: props.isHasAlbum.id.toString()
        };
    } else if (props.uploadingInfo) {
        defaultAlbum = {
            label: props.uploadingInfo.album?.label ? props.uploadingInfo.album?.label : '',
            value: props.uploadingInfo.album?.value ? props.uploadingInfo.album?.value : ''
        };
    } else if (defaultPreset.album?.label && defaultPreset.album.value) {
        defaultAlbum = {
            label: defaultPreset.album?.label,
            value: defaultPreset.album.value
        };
    }

    return (
        <div className={cn('input__sidebar', props.className && `${props.className}`)}>
            <UI.BoxHeader>Параметры загрузки фото</UI.BoxHeader>
            <InputYears
                registry={props.registry.field}
                name="upload_year"
                clearable={true}
                children={t('route.upload.sidebar.modal.year')}
                direction="column"
                onChange={onChangeYear}
                defaultValue={defaultYear}
                disabled={props.isPhotoPreview || !!props.isHasAlbum && !!props.isHasVideo || !!props.isHasVideo}
            />
            {elTournament}
            <InputForm
                className={cn('input__sidebar-price')}
                registry={props.registry.field}
                name="upload_price"
                children={t('route.upload.sidebar.modal.price')}
                onlyNumbers={true}
                direction="column"
                checkValidity={priceCheckValidity}
                defaultValue={defaultPreset.price}
            />
            <InputAlbums
                registry={props.registry.field}
                name="upload_album"
                children="Альбом*"
                direction="column"
                defaultValue={defaultAlbum}
                disabled={props.isPhotoPreview || !!props.isHasAlbum}
            />
            <span>Вы можете создать альбом в разделе <Link to={'/dashboard/photos'}>"Мои фотографии"</Link></span>
            {props.isLink && (
                <Checkbox
                    registry={props.registry.field}
                    name="recursive"
                    children={t('route.upload.sidebar.modal.recursive')}
                    className={cn('input__sidebar-price')}
                />
            )}
            {elSaveParams}
            {props.isPhotoPreview && (
                <Button className={cn('input__sidebar-continue_button')} to={`/albums/${defaultAlbum?.value}`}>
                    Завершить
                </Button>
            )}
        </div>
    );
};

export default Presets;
